import type { ReactNode } from 'react'
import Image from 'next/image'
import { getProxiedStoragePath, isRenderableImageUrl } from '@/lib/proxied-storage-path'
import { formatByline } from "@/lib/blog-byline"
import { NoImageFallback } from "@/components/ui/no-image-fallback"
// FRIDGE-239: this detector used to be inlined here AND copied
// character-for-character into app/blogs/news/[slug]/page-client.tsx. Two
// copies of one regex that decides how images render is one edit away from
// them disagreeing, so it now has a single home in lib/.
import { isFridgeFallbackImage } from "@/lib/fridge-fallback-image"

/**
 * Measured on production 09/27/2026: the homepage article grid pulled four
 * ORIGINAL blog hero PNGs through this card's plain <img> — 10.9 MB of the
 * page's 13 MB, one file 6 MB — with no lazy loading, so the page's load
 * event fired after 62 s on desktop and 110 s on /deals. A same-origin
 * storage path now goes through next/image: resized to the card, AVIF/WebP,
 * lazy by default. Anything else (an external host the optimizer may not
 * allow) keeps a plain <img>, now lazy, so no card can lose its picture.
 *
 * Fixed intrinsic size, NOT `fill` + `sizes`: with `sizes`, next/image writes
 * the full 15-candidate w-descriptor srcset (up to `&w=2048 … 2048w`) on every
 * card — the per-item payload scripts/html-render-gate.ts forbids on the home
 * page (FRIDGE-311; it failed PR #783's gate ×4). A fixed width gives a 1x/2x
 * pair instead: w=640 1x, w=828 2x for a card that renders about 300 px wide,
 * scaled to the 16:9 frame by CSS with object-contain.
 */
export const ARTICLE_CARD_IMAGE_WIDTH = 400
export const ARTICLE_CARD_IMAGE_HEIGHT = 225

export function isOptimizableCardImage(src: string): boolean {
  return src.startsWith("/") && !src.startsWith("//") && isRenderableImageUrl(src)
}

/**
 * FRIDGE-1272: the author photo is a 20 px circle, but the proxied files are
 * 640 px (27–39 KB) and one is a 1920 px original (114 KB) — 211 KB for four
 * avatars on the homepage. Same-origin photos go through the optimizer.
 *
 * The request size is 40, not 20. The portraits are landscape (3:2) and the
 * circle crops them square, so the image must be at least as TALL as the slot:
 * at 2x a 20 px circle needs 40 real pixels of height. Requested at 20, Next
 * serves w=48 for 2x, which is only 32 px tall, and the photo is upscaled —
 * measured on the local server 09/28/2026 (natural 24 x 16 CSS px). At 40 it
 * serves w=48 for 1x (32 px tall) and w=96 for 2x (64 px tall), about 1 KB
 * each. CSS keeps the 20 px circle.
 *
 * A path carrying a query string stays a plain <img>: next.config's
 * localPatterns only admit a query on the brand buckets, and an unmatched one
 * fails the BUILD, not the request.
 */
export const ARTICLE_CARD_AVATAR_SIZE = 20
export const ARTICLE_CARD_AVATAR_REQUEST_SIZE = 40

export function isOptimizableAvatar(src: string): boolean {
  return isOptimizableCardImage(src) && !src.includes("?")
}

interface ArticleCardProps {
  /** Article image URL */
  imageUrl?: string
  /** Existing optimized media renderer, when a content family requires one. */
  imageContent?: ReactNode
  /** Source-backed supplementary content beneath the shared metadata. */
  children?: ReactNode
  dateTimeZone?: string
  /** Source-backed alternative text when different from the headline. */
  imageAlt?: string
  /** Recorded reading estimate; absent estimates stay absent. */
  readTimeMinutes?: number
  /** Category badge text (e.g. "Buying Guide", "Review", "News") */
  badge?: string
  /** Article title */
  title: string
  /** Recorded person author; organizational bylines remain schema-only. */
  author?: string
  /**
   * The recorded author's avatar. Never substitute a different person's photo.
   */
  authorImageUrl?: string | null
  /** The single date to display — already formatted, no "Updated" prefix. */
  date?: string
  /** Short excerpt */
  excerpt?: string
  /** Link destination */
  href?: string
  /** Additional classes on the wrapper */
  className?: string
  /**
   * FRIDGE-1506: the card sits in the first screen, so its picture loads
   * without waiting for the lazy-load observer.
   */
  eager?: boolean
  /**
   * FRIDGE-1546: "high" on the first large picture of a first screen. Next
   * 16 no longer sets `fetchpriority` for an eager or `priority` image.
   */
  fetchPriority?: "high" | "low" | "auto"
  /**
   * FRIDGE-1504 L1504-9: the title's heading level, chosen by where the card
   * sits. Most lists put cards under a section h2, so the default stays h3; a
   * list placed directly under the page h1 passes 2, so no level is skipped.
   * Size and weight live on the classes, so the level never changes the look.
   */
  headingLevel?: ArticleCardHeadingLevel
}

export type ArticleCardHeadingLevel = 2 | 3 | 4

export function ArticleCard({
  imageUrl,
  imageContent,
  children,
  dateTimeZone,
  imageAlt,
  readTimeMinutes,
  badge,
  title,
  author,
  authorImageUrl,
  date,
  excerpt,
  href,
  className = "",
  eager = false,
  fetchPriority,
  headingLevel = 3,
}: ArticleCardProps) {
  const Wrapper = href ? "a" : "div"
  const Title = `h${headingLevel}` as const
  const wrapperProps = href ? { href } : {}

  // Owner rule 08/06/2026 (FRIDGE-131): a visible byline is a person's name or
  // nothing. Feeders pass fallbacks like "Fridge.com" when author_name is
  // empty — those (and "Fridge.com Editorial Team") are schema-layer names and
  // never render here, so the filter lives in the card, once, for every feeder.
  const displayAuthor = formatByline(author)

  // Callers pass raw DB timestamps (ISO 8601); readers get "March 7, 2026",
  // never "2026-03-07T12:22:12.031061+00:00". Unparseable input renders nothing.
  const parsedDate = date ? new Date(date) : null
  // Stored ISO publication dates use UTC; preformatted calendar labels retain
  // their already-selected day instead of shifting when parsed locally.
  const displayTimeZone = dateTimeZone ?? (/^\d{4}-\d{2}-\d{2}/.test(date ?? '') ? 'UTC' : undefined)
  const displayDate = parsedDate && !isNaN(parsedDate.getTime())
    ? parsedDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", ...(displayTimeZone ? { timeZone: displayTimeZone } : {}) })
    : null

  // Contained rendering for EVERY image (plan §0 item 177 — owner: "images
  // look bad. cut off. and not full image"): the full hero always fits inside
  // the fixed 16:9 frame on a light neutral letterbox instead of a blind
  // cover-crop (which produced door-handle zooms and cut-off frames).
  // Fallback logos keep the white padded treatment so the mark breathes.
  const isFallback = isFridgeFallbackImage(imageUrl)
  const imageSrc = getProxiedStoragePath(imageUrl) ?? ""
  const authorImageSrc = authorImageUrl ? getProxiedStoragePath(authorImageUrl) ?? "" : ""
  // Owner alt-text rule (plan §0 item 143): alt text ends with " | Fridge.com".
  const rawAlt = imageAlt || title
  const altText = rawAlt.endsWith(" | Fridge.com") ? rawAlt : `${rawAlt} | Fridge.com`
  return (
    <Wrapper
      {...wrapperProps}
      data-article-card
      className={`group block bg-white rounded-lg overflow-hidden shadow-[0_2px_12px_rgba(0,25,69,0.04)] hover:shadow-[0_12px_40px_rgba(0,25,69,0.06)] transition-shadow ${className}`}
    >
      {/* Image area */}
      <div
        className={`relative aspect-[16/9] overflow-hidden ${
          isFallback ? "bg-white p-6" : "bg-gray-50"
        }`}
      >
        {imageContent ?? (imageUrl ? (
          isOptimizableCardImage(imageSrc) ? (
            <Image
              src={imageSrc}
              alt={altText}
              width={ARTICLE_CARD_IMAGE_WIDTH}
              height={ARTICLE_CARD_IMAGE_HEIGHT}
              loading={eager ? "eager" : undefined}
              fetchPriority={fetchPriority}
              className="w-full h-full object-contain"
            />
          ) : (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={imageSrc}
              alt={altText}
              loading={eager ? "eager" : "lazy"}
              fetchPriority={fetchPriority}
              decoding="async"
              className="w-full h-full object-contain"
            />
          )
        ) : (
          // FRIDGE-239 — was a flat grey gradient; now the shared brand mark.
          <NoImageFallback />
        ))}
        {badge && (
          <span className="absolute bottom-2 left-2 bg-[#ff9951] text-[var(--color-brand-action-text)] text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
            {badge}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <Title className="text-sm font-bold text-gray-900 line-clamp-2 mb-1 group-hover:text-[#0469f0] transition-colors">
          {title}
        </Title>
        {/* FRIDGE-1186: the same person photo, name and date on every list.
            Missing attribution stays absent rather than inventing an author. */}
        {(displayAuthor || displayDate) && (
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-2">
            {displayAuthor && authorImageSrc && (
              isOptimizableAvatar(authorImageSrc) ? (
                <Image
                  src={authorImageSrc}
                  alt={`${displayAuthor} | Fridge.com`}
                  width={ARTICLE_CARD_AVATAR_REQUEST_SIZE}
                  height={ARTICLE_CARD_AVATAR_REQUEST_SIZE}
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-gray-200"
                />
              ) : (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={authorImageSrc}
                  alt={`${displayAuthor} | Fridge.com`}
                  width={ARTICLE_CARD_AVATAR_SIZE}
                  height={ARTICLE_CARD_AVATAR_SIZE}
                  loading="lazy"
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-gray-200"
                />
              )
            )}
            {displayAuthor && <span className="text-xs text-gray-600">{displayAuthor}</span>}
            {displayDate && <span className="text-xs text-gray-500">{displayDate}</span>}
          </div>
        )}
        {typeof readTimeMinutes === "number" && Number.isFinite(readTimeMinutes) && readTimeMinutes > 0 && (
          <p className="text-xs text-gray-500 mb-2">{readTimeMinutes} min read</p>
        )}
        {excerpt && (
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{excerpt}</p>
        )}
        {children}
      </div>
    </Wrapper>
  )
}
